import React, { useState } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download, Loader2 } from 'lucide-react';

const DaybookReport = () => {
    const [loading, setLoading] = useState(false);
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    const formatAmount = (val) => {
        return Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const generatePDF = async () => {
        setLoading(true);
        try {
            const response = await API.get(`/v1/rep/daybook-report`, {
                params: { startDate, endDate }
            });
            
            const { transactions, openingCashBalance, closingCashBalance, summary } = response.data;

            if (!transactions || transactions.length === 0) {
                alert("No transactions found for the selected date range!");
                setLoading(false);
                return;
            }

            const doc = new jsPDF('l', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();

            // Header Section
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text("UNITED AUTOS", pageWidth / 2, 12, { align: 'center' });
            
            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.text(`From : ${startDate.split('-').reverse().join('/')}`, pageWidth - 45, 12);
            doc.text(`To : ${endDate.split('-').reverse().join('/')}`, pageWidth - 45, 17);

            doc.setLineWidth(0.4);
            doc.line(15, 22, pageWidth - 15, 22);

            // Opening Cash Balance
            doc.setFont("helvetica", "bold");
            doc.text(`Opening Cash Balance :`, pageWidth - 90, 28);
            doc.text(`${formatAmount(openingCashBalance)}`, pageWidth - 15, 28, { align: 'right' });

            const rows = [];
            let currentVoucherType = '';

            transactions.forEach((tx) => {
                if (tx.voucher_type !== currentVoucherType) {
                    currentVoucherType = tx.voucher_type;
                    rows.push([
                        { 
                            content: currentVoucherType ? currentVoucherType.toUpperCase() : 'GENERAL', 
                            colSpan: 8, 
                            styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: [0, 0, 0] } 
                        }
                    ]);
                }

                // Balance with Dr/Cr format e.g., "78,000.00 Dr"
                const formattedBalance = `${formatAmount(tx.balance)} ${tx.balanceType}`;

                rows.push([
                    tx.v_no || '',
                    tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '',
                    tx.code || '', // Account Number instead of ID
                    tx.account_title || '',
                    Number(tx.debit || 0) ? formatAmount(tx.debit) : '0.00',
                    Number(tx.credit || 0) ? formatAmount(tx.credit) : '0.00',
                    formattedBalance, // Running Balance with Dr/Cr
                    tx.remarks || ''
                ]);
            });

            autoTable(doc, {
                startY: 32,
                head: [[
                    { content: 'V. No', styles: { halign: 'left' } },
                    { content: 'Date', styles: { halign: 'left' } },
                    { content: 'Account Number', styles: { halign: 'left' } },
                    { content: 'Title Of A/c', styles: { halign: 'left' } },
                    { content: 'Debit', styles: { halign: 'right' } },
                    { content: 'Credit', styles: { halign: 'right' } },
                    { content: 'Bal Amt', styles: { halign: 'right' } },
                    { content: 'Remarks', styles: { halign: 'left' } }
                ]],
                body: rows,
                theme: 'plain',
                headStyles: { fontSize: 8.5, fontStyle: 'bold', textColor: [0, 0, 0], lineWidth: { bottom: 0.4 }, lineColor: [0, 0, 0] },
                styles: { fontSize: 8, cellPadding: 1.5, textColor: [0, 0, 0] },
                columnStyles: {
                    0: { cellWidth: 18 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 22 },
                    3: { cellWidth: 65 },
                    4: { cellWidth: 25, halign: 'right' },
                    5: { cellWidth: 25, halign: 'right' },
                    6: { cellWidth: 28, halign: 'right' },
                    7: { cellWidth: 'auto' }
                },
                margin: { left: 15, right: 15 }
            });

            const finalY = doc.lastAutoTable.finalY + 6;
            doc.setLineWidth(0.3);
            doc.line(15, finalY, pageWidth - 15, finalY);

            // Grand Total & Closing Balance
            doc.setFont("helvetica", "bold");
            doc.text("Grand Total:", 105, finalY + 6);
            doc.text(formatAmount(summary?.totalDebit), 155, finalY + 6, { align: 'right' });
            doc.text(formatAmount(summary?.totalCredit), 180, finalY + 6, { align: 'right' });

            // Closing Cash Box
            doc.rect(pageWidth - 95, finalY + 12, 80, 8);
            doc.text("Cash Closing Bal:", pageWidth - 92, finalY + 17);
            doc.text(formatAmount(closingCashBalance), pageWidth - 18, finalY + 17, { align: 'right' });

            doc.save(`Daybook_${startDate}_to_${endDate}.pdf`);
        } catch (err) {
            console.error("Export Error:", err);
            alert("Failed to export PDF.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '40px', maxWidth: '650px', margin: '40px auto', background: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
            <h3 style={{ marginBottom: '8px', color: '#1e293b' }}>Daybook Report Generator</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '25px' }}>Dynamic Ledger with Running Balances (Dr/Cr)</p>
            
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '25px' }}>
                <div style={{ textAlign: 'left' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#334155' }}>From Date:</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }} />
                </div>
                <div style={{ textAlign: 'left' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#334155' }}>To Date:</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }} />
                </div>
            </div>

            <button 
                onClick={generatePDF} 
                disabled={loading} 
                style={{ width: '100%', padding: '13px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '15px' }}
            >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} Download Daybook PDF Report
            </button>
        </div>
    );
};

export default DaybookReport;